import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckSquareOutlined, InfoCircleFilled, WarningFilled } from '@ant-design/icons';
import { BaseAlertBanner, BaseButton, BaseCheckbox, BaseModal, BaseTooltip, HtmlInjection } from '@components';
import { BORDER_COLORS } from '@constants/colors.ts';
import { useAppDispatch } from '@hooks/reduxHooks.ts';
import { useCustomNavigate } from '@hooks/useCustomNavigate.ts';
import { useLingui } from '@lingui/react/macro';
import { ROUTES } from '@router/constants.ts';
import { DescText, ItemText, TooltipTitleWrapper } from '@routes/___shared___/styled.tsx';
import type { UserInfoConsent } from '@routes/___shared___/user.api.ts';
import { updateUserConsentInfo } from '@routes/auth/login/model/login.slice.ts';
import type { CheckboxChangeEvent } from 'antd';

import {
    ContentWrapper,
    FOOTER_BTN_STYLE,
    GradientOverlay,
    ItemRow,
    ListContainer,
    modalForceStyles,
    ScrollWrapper,
    SELECT_ALL_BTN_STYLE,
    TitleWrapper,
} from './styled.ts';

interface Props {
    userInfoConsents: UserInfoConsent[];
    open: boolean;
    onClose(): void;
    redirectUrl: string;
}

export const ConsentModal: React.FC<Props> = ({ userInfoConsents, open, onClose, redirectUrl }) => {
    const { t } = useLingui();
    const dispatch = useAppDispatch();
    const navigate = useCustomNavigate();
    const listRef = useRef<HTMLDivElement | null>(null);
    const isSubmittingRef = useRef(false);

    const INITIAL_STATE = useMemo(
        () =>
            userInfoConsents.reduce<Record<number, boolean>>((acc, c) => {
                acc[c.id] = c.isApproved;
                return acc;
            }, {}),
        [userInfoConsents],
    );

    const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>(() => INITIAL_STATE);
    const [isLoading, setLoading] = useState(false);
    const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
    const allChecked = Object.values(checkedItems).every(Boolean);

    const CONSENT_CENTER = t`Consent Center`;
    const CONSENT_UPDATE_REQUIRED = t`Consent Update Required`;
    const BUTTON_CONTENT_ACCEPT = t`Accept consent`;
    const BUTTON_CONTENT_UPDATE = t`Save Updated Consents`;
    const SELECT_BUTTON_CONTENT = allChecked ? t`Deselect All` : t`Select All`;
    const TOOLTIP_CONTENT = t`All items must be selected to continue`;
    const WARNING_DESCRIPTION = t`Before proceeding, please review and acknowledge each item below. All consents must be checked, and the information section confirmed, to continue with registration.`;
    const INFO_DESCRIPTION = t`By proceeding, you acknowledge that you have read and understood this disclaimer, and consent to all the terms described above.`;

    const scrollable = userInfoConsents.length > 7;
    const isInitial = userInfoConsents.every((c) => c.approvedDateUtc === null);
    const MODAL_TITLE = isInitial ? CONSENT_CENTER : CONSENT_UPDATE_REQUIRED;
    const BUTTON_TITLE = isInitial ? BUTTON_CONTENT_ACCEPT : BUTTON_CONTENT_UPDATE;
    const TOOLTIP_TITLE = !allChecked ? <TooltipTitleWrapper>{TOOLTIP_CONTENT}</TooltipTitleWrapper> : null;

    useEffect(() => {
        setCheckedItems(INITIAL_STATE);
    }, [INITIAL_STATE]);

    const handleChange = (id: number) => (e: CheckboxChangeEvent) => {
        const checked = e.target.checked;
        setCheckedItems((prev) => ({ ...prev, [id]: checked }));
    };

    const handleContinue = () => {
        if (!allChecked || isSubmittingRef.current) return;

        isSubmittingRef.current = true;

        const previouslyApprovedIds = userInfoConsents.filter((c) => c.isApproved).map((c) => c.id);

        const allCheckedIds = Object.entries(checkedItems)
            .filter(([, v]) => v)
            .map(([k]) => Number(k));

        const newApprovedIds = allCheckedIds.filter((id) => !previouslyApprovedIds.includes(id));

        setLoading(true);
        dispatch(updateUserConsentInfo({ accepted: newApprovedIds }))
            .then(() => {
                navigate(redirectUrl, { state: { from: ROUTES.AUTH.LOGIN } });
            })
            .catch(() => {})
            .finally(() => {
                isSubmittingRef.current = false;
                setLoading(false);
            });
    };

    const handleSelectAll = () => {
        if (!allChecked) {
            const newChecked: Record<number, boolean> = {};
            userInfoConsents.forEach((c) => {
                newChecked[c.id] = true;
            });
            setCheckedItems(newChecked);
        } else {
            setCheckedItems(INITIAL_STATE);
        }
    };

    const handleConsentsScroll = () => {
        const el = listRef.current;
        if (!el) return;

        // Consider bottom reached within 2px to handle rounding issues
        const threshold = 2;

        setIsScrolledToBottom(el.scrollHeight - el.scrollTop - el.clientHeight <= threshold);
    };

    return (
        <BaseModal
            width={800}
            open={open}
            title={<TitleWrapper>{MODAL_TITLE}</TitleWrapper>}
            onCancel={onClose}
            closable
            styles={{ mask: modalForceStyles.mask }}
            content={
                <ContentWrapper>
                    <BaseAlertBanner description={WARNING_DESCRIPTION} type="warning" icon={<WarningFilled />} />
                    <ScrollWrapper>
                        <ListContainer ref={listRef} $scrollable={scrollable} onScroll={handleConsentsScroll}>
                            {userInfoConsents.map((consent) => {
                                return (
                                    <ItemRow key={consent.id} $wasConsentApproved={consent.isApproved}>
                                        <ItemText>
                                            {!!(consent.title && consent.description) && (
                                                <DescText>
                                                    <HtmlInjection
                                                        text={`<b>${consent.title}</b> ${consent.description}`}
                                                    />
                                                </DescText>
                                            )}
                                        </ItemText>
                                        <BaseCheckbox
                                            checked={checkedItems[consent.id]}
                                            onChange={handleChange(consent.id)}
                                            disabled={Boolean(consent.approvedDateUtc)}
                                        />
                                    </ItemRow>
                                );
                            })}
                        </ListContainer>
                        {!!(scrollable && !isScrolledToBottom) && <GradientOverlay />}
                    </ScrollWrapper>
                    <BaseAlertBanner description={INFO_DESCRIPTION} type="info" icon={<InfoCircleFilled />} />
                    <BaseButton
                        style={SELECT_ALL_BTN_STYLE}
                        $customType="outline"
                        onClick={handleSelectAll}
                        iconPlacement="end"
                        icon={<CheckSquareOutlined />}
                    >
                        {SELECT_BUTTON_CONTENT}
                    </BaseButton>
                </ContentWrapper>
            }
            footer={
                <>
                    <BaseButton style={FOOTER_BTN_STYLE} $customType="outline" onClick={onClose}>
                        {t`Cancel`}
                    </BaseButton>
                    <BaseTooltip title={TOOLTIP_TITLE} placement="bottomLeft" color={BORDER_COLORS.white}>
                        <BaseButton
                            style={FOOTER_BTN_STYLE}
                            $customType="primary"
                            onClick={handleContinue}
                            disabled={!allChecked || isLoading}
                            loading={isLoading}
                        >
                            {BUTTON_TITLE}
                        </BaseButton>
                    </BaseTooltip>
                </>
            }
        />
    );
};
